/* my-products.js: render the signed-in customer's licenses from the Shopify App Proxy.
   Zero deps, same house style as store.js. Everything is escaped — none of this data is ours.

   The server (/proxy/licenses) decides who you are from Shopify's SIGNED request; this file never
   sends an id and never trusts one. If you're signed out the proxy says so and we show the sign-in
   prompt — we don't try to guess from Liquid's `customer`, which is unreliable under new customer
   accounts. */
(function () {
  var root = document.getElementById("jiggly-licenses");
  if (!root) return;

  var endpoint = root.getAttribute("data-endpoint") || "/apps/jiggly/licenses";
  var loginUrl = root.getAttribute("data-login-url") || "/account/login";
  var emptyText = root.getAttribute("data-empty") || "No licenses on this account yet.";
  var loggedOutText = root.getAttribute("data-logged-out") || "Sign in to see your licenses.";

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function fmtDate(iso) {
    if (!iso) return "never";
    var d = new Date(iso);
    if (isNaN(d)) return esc(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  function card(html) { return '<div class="card">' + html + "</div>"; }

  function statusChip(status, expiry) {
    // Expiry is interpreted here the same way the app does: a date in the past reads as lapsed even
    // while Keygen still calls the license ACTIVE (docs/LICENSING.md §8).
    var lapsed = expiry && new Date(expiry) < new Date();
    var label = lapsed ? "EXPIRED" : esc(status || "ACTIVE");
    var bg = (label === "ACTIVE") ? "var(--lime)" : "var(--cherry)";
    var fg = (label === "ACTIVE") ? "#000" : "#fff";
    return '<span class="chip" style="background:' + bg + ";color:" + fg + '">' + label + "</span>";
  }

  function machineRow(lic, m) {
    var name = esc(m.name || m.fingerprint || "Unknown machine");
    var seen = m.last_seen ? " · last seen " + fmtDate(m.last_seen) : "";
    return '<li class="mp-machine">' +
             "<span>" + name + '<small style="opacity:.7">' + esc(seen) + "</small></span>" +
             '<button class="btn" type="button" data-license="' + esc(lic.id) + '" ' +
                     'data-machine="' + esc(m.id) + '">Deactivate</button>' +
           "</li>";
  }

  function licenseCard(lic) {
    var used = lic.seats_used == null ? "?" : lic.seats_used;
    var max = lic.seats_max == null ? "?" : lic.seats_max;
    var machines = (lic.machines || []).length
      ? '<ul class="mp-machines">' + lic.machines.map(function (m) { return machineRow(lic, m); }).join("") + "</ul>"
      : '<p class="mp-none">No machines activated yet.</p>';
    return card(
      '<div class="mp-head">' + statusChip(lic.status, lic.expiry) +
        '<span class="mp-seats">' + esc(used) + " of " + esc(max) + " seats used</span>" +
      "</div>" +
      '<label class="mp-label">License key</label>' +
      '<div class="mp-key"><code>' + esc(lic.key) + "</code>" +
        '<button class="btn" type="button" data-copy="' + esc(lic.key) + '">Copy</button>' +
      "</div>" +
      '<p class="mp-expiry">Renews / expires <strong>' + fmtDate(lic.expiry) + "</strong></p>" +
      machines
    );
  }

  function render(data) {
    if (!data.logged_in) {
      root.innerHTML = card("<p>" + esc(loggedOutText) + "</p>" +
        '<a class="btn cherry" href="' + esc(loginUrl) + '">Sign in</a>');
      return;
    }
    var licenses = data.licenses || [];
    if (!licenses.length) {
      root.innerHTML = card("<p>" + esc(emptyText) + "</p>");
      return;
    }
    var dl = data.download_url
      ? '<a class="btn cherry" href="' + esc(data.download_url) + '">Download Jiggly</a>' : "";
    root.innerHTML = licenses.map(licenseCard).join("") + (dl ? card(dl) : "");
  }

  function fail(msg) { root.innerHTML = card("<p>" + esc(msg) + "</p>"); }

  fetch(endpoint, { headers: { Accept: "application/json" }, credentials: "same-origin" })
    .then(function (r) {
      // A non-JSON body here almost always means the request never reached us — Shopify's password
      // page or a proxy misconfig answered instead. Say that plainly rather than "unexpected token".
      var ct = r.headers.get("content-type") || "";
      if (ct.indexOf("application/json") === -1) {
        throw new Error("The store couldn't reach the license service. If the store is password-protected, that page answered instead.");
      }
      return r.json();
    })
    .then(function (d) {
      if (d && d.ok === false) throw new Error(d.message || d.error || "Couldn't load your licenses.");
      render(d || {});
    })
    .catch(function (e) { fail(e.message || "Couldn't load your licenses right now."); });

  // Copy + deactivate, delegated (the cards are rendered after this runs).
  root.addEventListener("click", function (ev) {
    var btn = ev.target.closest("button");
    if (!btn) return;

    var key = btn.getAttribute("data-copy");
    if (key) {
      navigator.clipboard.writeText(key).then(function () {
        var old = btn.textContent; btn.textContent = "Copied";
        setTimeout(function () { btn.textContent = old; }, 1200);
      });
      return;
    }

    var lic = btn.getAttribute("data-license");
    var machine = btn.getAttribute("data-machine");
    if (!lic || !machine) return;
    // Irreversible-ish (they'd have to re-activate on that machine), so confirm.
    if (!window.confirm("Free this seat? That machine will need to activate again to use Jiggly.")) return;
    btn.disabled = true; btn.textContent = "…";
    fetch(endpoint.replace(/\/licenses$/, "/deactivate"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ license_id: lic, machine_id: machine })
    })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || d.ok !== true) throw new Error((d && (d.message || d.error)) || "Couldn't free that seat.");
        var row = btn.closest(".mp-machine");
        if (row) row.remove();
      })
      .catch(function (e) {
        btn.disabled = false; btn.textContent = "Deactivate";
        window.alert(e.message || "Couldn't free that seat.");
      });
  });
})();
